import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { setMainSelection, useEditingStateWithCtx } from '@/atoms'
import { setPendingFitMap } from '@/map'
import { useNavigate } from '@/routing'

import { useFitInOrderModal } from '@/components/modals/FitInOrderModal'
import { useResidentModalsApi } from '@/components/modals/ResidentModals'
import { useUnassignOrderModal } from '@/components/modals/UnassignOrderModal'

import { type ExportOrderSource } from '@/components/modals/ExportOrderModal'
import { useOrderStepsNavigator } from '../../../../../hooks/useOrderStepsNavigator'

import { selectCalendarRange } from '@/features/domain/ui'
import { selectUserConfiguration } from '@/features/domain/user'

interface OrderAccumulator {
  unassignedOrders: uui.domain.client.rm.ExtendedOrderStep[]
  assignedOrders: uui.domain.client.rm.ExtendedOrderStep[]
  unassignedOrderIds: string[]
  assignedOrderIds: string[]
}

export const useActions = () => {
  const { currentOrders, currentOrderIds } = useOrderStepsNavigator()

  const { unassignedOrders, unassignedOrderIds, assignedOrders, assignedOrderIds } = useMemo(
    () =>
      currentOrders.reduce<OrderAccumulator>(
        (acc, order) => {
          if (order.isUnassigned) {
            acc.unassignedOrderIds.push(order.orderStep.id)
            acc.unassignedOrders.push(order)
          } else {
            acc.assignedOrderIds.push(order.orderStep.id)
            acc.assignedOrders.push(order)
          }

          return acc
        },
        {
          unassignedOrderIds: [],
          assignedOrderIds: [],
          unassignedOrders: [],
          assignedOrders: [],
        },
      ),
    [currentOrders],
  )

  const { planType } = useSelector(selectUserConfiguration)
  const calendarRange = useSelector(selectCalendarRange)
  const date = planType === 'archived' ? calendarRange.minDate : undefined

  const { setEditing } = useEditingStateWithCtx('order')
  const navigate = useNavigate(true)

  const { show: showFitInOrderModal, Modal: FitInOrderModal } = useFitInOrderModal()
  const {
    exportOrders: { show: showExportOrderModal },
  } = useResidentModalsApi()

  const { show: showUnassignOrderModal, Modal: UnassignOrderModal } = useUnassignOrderModal()
  const {
    deleteOrders: { show: handleDeleteOrders },
    duplicateOrders: { show: handleDuplicateOrders },
    copySelectedOrders: { show: handleCopySelectedOrders },
  } = useResidentModalsApi()

  const goToEdit = useCallback(() => {
    // Set the new main selection
    setMainSelection('orderSteps', currentOrderIds)

    // Set the order form to editing mode
    setEditing(currentOrderIds)

    // queue a pending fitMap request
    // it will be applied by the destination view when it opens
    const latLngCoordinates = currentOrders.map(order => order.latLng)
    setPendingFitMap(latLngCoordinates)

    // Navigate to the correct route
    navigate('orders')
  }, [currentOrders, currentOrderIds, navigate, setEditing])

  const showDeleteOrderModal = useCallback(() => {
    handleDeleteOrders(currentOrderIds)
  }, [currentOrderIds, handleDeleteOrders])

  const showCopySelectedOrdersModal = useCallback(() => {
    handleCopySelectedOrders(currentOrderIds)
  }, [currentOrderIds, handleCopySelectedOrders])

  const showDuplicateOrderModal = useCallback(() => {
    handleDuplicateOrders(currentOrderIds)
  }, [currentOrderIds, handleDuplicateOrders])

  return {
    currentOrders,
    assignedOrders,
    unassignedOrders,
    currentOrderIds,
    actions: {
      goToEdit,
      showCopySelectedOrdersModal,
      showFitInOrderModal,
      showExportOrderModal: (source: ExportOrderSource) =>
        showExportOrderModal(source, currentOrderIds, currentOrders, date, planType),
      showDeleteOrderModal,
      showUnassignOrderModal,
      showDuplicateOrderModal,
    },
    modals: (
      <>
        <FitInOrderModal orderStepIds={unassignedOrderIds} />
        <UnassignOrderModal orderStepIds={assignedOrderIds} />
      </>
    ),
  }
}
